<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

import { dayLabel, type PublishedDay } from '../../api/demo-client.js';

/**
 * Campo de dia com calendário mínimo. Somente Hoje (`today`, data do servidor)
 * e os dias publicados devolvidos pelo servidor são botões habilitados;
 * lacunas entre eles e dias fora da lista ficam desabilitados e `choose`
 * recusa qualquer outro valor. Hoje aparece uma vez só, mesmo publicado.
 * Não há digitação.
 *
 * A grade trabalha só com as strings `YYYY-MM-DD` (aritmética em UTC): o fuso
 * do navegador não entra em nenhum cálculo, e o instante histórico vem pronto
 * do servidor.
 *
 * Os dias chegam sob demanda: abrir avisa `open` e trocar de mês avisa
 * `month`, ambos com o primeiro dia do mês exibido, para o pai carregar as
 * páginas até ele. `hasOlder` mantém "Mês anterior" habilitado enquanto o
 * servidor ainda tem dias mais antigos que os carregados.
 */
const props = defineProps<{
  days: readonly PublishedDay[];
  today: string | null;
  modelValue: string | null;
  hasOlder?: boolean;
  loading?: boolean;
  failed?: boolean;
}>();
const emit = defineEmits<{ 'update:modelValue': [date: string]; open: [monthStart: string]; month: [monthStart: string] }>();

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro',
  'outubro', 'novembro', 'dezembro'];

const open = ref(false);
const trigger = ref<HTMLButtonElement | null>(null);
const panel = ref<HTMLElement | null>(null);
const alignRight = ref(false);
const month = ref<{ year: number; month: number }>({ year: 2000, month: 0 });

const available = computed(() => new Set([...(props.today === null ? [] : [props.today]), ...props.days.map((day) => day.date)]));
const range = computed(() => {
  const sorted = [...available.value].sort();
  return { min: sorted[0]?.slice(0, 7) ?? null, max: sorted[sorted.length - 1]?.slice(0, 7) ?? null };
});
const monthKey = computed(() => `${month.value.year}-${String(month.value.month + 1).padStart(2, '0')}`);
const cells = computed(() => {
  const { year, month: index } = month.value;
  const lead = new Date(Date.UTC(year, index, 1)).getUTCDay();
  const count = new Date(Date.UTC(year, index + 1, 0)).getUTCDate();
  const result: ({ date: string; day: number } | null)[] = Array.from({ length: lead }, () => null);
  for (let day = 1; day <= count; day += 1) {
    result.push({ date: `${monthKey.value}-${String(day).padStart(2, '0')}`, day });
  }
  return result;
});

function showMonthOf(date: string): void {
  month.value = { year: Number(date.slice(0, 4)), month: Number(date.slice(5, 7)) - 1 };
}

function shift(delta: number): void {
  const next = new Date(Date.UTC(month.value.year, month.value.month + delta, 1));
  month.value = { year: next.getUTCFullYear(), month: next.getUTCMonth() };
  emit('month', `${monthKey.value}-01`);
}

async function toggle(): Promise<void> {
  if (open.value) return close();
  const start = props.modelValue ?? props.today ?? props.days[0]?.date;
  if (start === undefined) return;
  showMonthOf(start);
  emit('open', `${monthKey.value}-01`);
  alignRight.value = false;
  open.value = true;
  await nextTick();
  // Sem espaço à direita (gatilho perto da borda), o painel abre para a esquerda.
  if (panel.value !== null && panel.value.getBoundingClientRect().right > window.innerWidth - 8) alignRight.value = true;
  const target = panel.value?.querySelector<HTMLElement>('[aria-pressed="true"]')
    ?? panel.value?.querySelector<HTMLElement>('.day:not([disabled])');
  target?.focus();
}

function close(): void {
  open.value = false;
  trigger.value?.focus();
}

function label(date: string): string {
  return date === props.today ? `Hoje — ${dayLabel(date)}` : dayLabel(date);
}

function choose(date: string): void {
  if (!available.value.has(date)) return;
  emit('update:modelValue', date);
  close();
}

function onOutside(event: MouseEvent): void {
  const root = trigger.value?.parentElement;
  if (root !== null && root !== undefined && !root.contains(event.target as Node)) open.value = false;
}

watch(open, (value) => {
  if (value) document.addEventListener('mousedown', onOutside);
  else document.removeEventListener('mousedown', onOutside);
});
onBeforeUnmount(() => document.removeEventListener('mousedown', onOutside));
</script>

<template>
  <div class="day-picker">
    <span
      id="day-picker-label"
      class="field-label"
    >Dia</span>
    <button
      ref="trigger"
      type="button"
      class="day-picker__trigger"
      aria-haspopup="dialog"
      :aria-expanded="open"
      aria-labelledby="day-picker-label day-picker-value"
      :disabled="available.size === 0"
      @click="toggle"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        width="16"
        height="16"
      ><rect
        x="3.5"
        y="5"
        width="17"
        height="15.5"
        rx="2.5"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
      /><path
        d="M3.5 10h17M8 3v4M16 3v4"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
      /></svg>
      <span id="day-picker-value">{{ modelValue === null ? 'Sem leituras' : label(modelValue) }}</span>
    </button>
    <div
      v-if="open"
      ref="panel"
      class="day-picker__panel"
      :class="{ 'day-picker__panel--right': alignRight }"
      role="dialog"
      aria-label="Escolher dia"
      @keydown.esc.prevent.stop="close"
    >
      <div class="day-picker__nav">
        <button
          type="button"
          aria-label="Mês anterior"
          :disabled="loading || ((range.min === null || monthKey <= range.min) && !hasOlder)"
          @click="shift(-1)"
        >
          ‹
        </button>
        <span aria-live="polite">{{ MONTHS[month.month] }} de {{ month.year }}</span>
        <button
          type="button"
          aria-label="Próximo mês"
          :disabled="range.max === null || monthKey >= range.max"
          @click="shift(1)"
        >
          ›
        </button>
      </div>
      <div class="day-picker__grid">
        <span
          v-for="(weekday, index) in WEEKDAYS"
          :key="`w${index}`"
          class="weekday"
          aria-hidden="true"
        >{{ weekday }}</span>
        <template
          v-for="(cell, index) in cells"
          :key="cell?.date ?? `pad${index}`"
        >
          <span v-if="cell === null" />
          <button
            v-else
            type="button"
            class="day"
            :data-date="cell.date"
            :disabled="!available.has(cell.date)"
            :aria-pressed="cell.date === modelValue"
            :aria-label="label(cell.date)"
            @click="choose(cell.date)"
          >
            {{ cell.day }}
          </button>
        </template>
      </div>
      <p
        v-if="loading"
        class="day-picker__status"
        role="status"
      >
        Carregando dias…
      </p>
      <p
        v-else-if="failed"
        class="day-picker__status"
        role="alert"
      >
        Não foi possível carregar os dias.
        <button
          type="button"
          class="day-picker__retry"
          @click="emit('open', `${monthKey}-01`)"
        >
          Tentar novamente
        </button>
      </p>
      <button
        v-if="today !== null"
        type="button"
        class="day-picker__today"
        :aria-pressed="today === modelValue"
        @click="choose(today)"
      >
        Hoje
      </button>
    </div>
  </div>
</template>

<style scoped>
.day-picker { position: relative; display: flex; flex-direction: column; gap: 0.2rem; }
.day-picker__trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 9.5rem;
  height: 2.5rem;
  padding: 0 0.75rem;
  border: 1px solid var(--border);
  border-radius: calc(var(--radius) - 4px);
  background: #fff;
  color: var(--foreground);
  font: inherit;
  cursor: pointer;
}
.day-picker__trigger:disabled { color: var(--muted-foreground); cursor: not-allowed; }
.day-picker__panel {
  position: absolute;
  top: calc(100% + 0.4rem);
  left: 0;
  z-index: 1500;
  width: 17rem;
  padding: 0.75rem;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: 0 12px 30px hsl(222 47% 11% / 0.14);
}
.day-picker__panel--right { left: auto; right: 0; }
.day-picker__nav { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; font-weight: 600; }
.day-picker__nav button {
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  color: var(--foreground);
  font-size: 1.1rem;
  cursor: pointer;
}
.day-picker__nav button:disabled { opacity: 0.4; cursor: not-allowed; }
.day-picker__grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 0.2rem; text-align: center; }
.weekday { font-size: 0.75rem; color: var(--muted-foreground); padding: 0.2rem 0; }
.day {
  height: 2.1rem;
  border: 1px solid transparent;
  border-radius: 8px;
  background: var(--secondary);
  color: var(--foreground);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.day:hover:not(:disabled) { border-color: var(--primary); }
.day:disabled { background: transparent; color: hsl(215 16% 70%); font-weight: 400; cursor: not-allowed; }
.day[aria-pressed='true'] { background: var(--primary-strong); color: #fff; }
.day-picker__today {
  width: 100%;
  height: 2.1rem;
  margin-top: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  color: var(--foreground);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.day-picker__today:hover { border-color: var(--primary); }
.day-picker__status { margin: 0.5rem 0 0; font-size: 0.8rem; color: var(--muted-foreground); }
.day-picker__retry { margin-left: 0.25rem; padding: 0; border: 0; background: none; color: var(--primary-strong); font: inherit; text-decoration: underline; cursor: pointer; }
.day-picker__today[aria-pressed='true'] { background: var(--primary-strong); border-color: var(--primary-strong); color: #fff; }
@media (max-width: 480px) {
  .day-picker__panel { width: min(17rem, calc(100vw - 2rem)); }
}
</style>
